// The video page of a recording (PRD-0074, slice 03): the playback copy's ticketed URL, the
// tracks the engine returned, and the one shared state that ties the rail to the player — the
// selected pass, and the pass "Play pass" asked for. Every fetch lives here; the player and the
// strip under `components/` only draw (ADR-0039).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { pureTimelineKeys } from '@/components/pure-timeline/usePureTimelineData'
import { canDecideFaces } from '@/features/face-matching/decision-role'
import { ApiError } from '@/services/api/client'
import {
  type VideoPlateTrack,
  type VideoPlaybackResponse,
  type VideoTrack,
  videoApi,
} from '@/services/api/video'
import { type RecordedTime, recordedTimeOf } from '../recording-time'
import {
  type PassPlayRequest,
  passPlayRequest,
  spanPlayRequest,
  trackOfFace,
} from '../video-tracks'

export const videoKeys = {
  all: (caseId: string) => ['case', caseId, 'video'] as const,
  playback: (caseId: string, documentId: string) =>
    [...videoKeys.all(caseId), documentId, 'playback'] as const,
  tracks: (caseId: string, documentId: string) =>
    [...videoKeys.all(caseId), documentId, 'tracks'] as const,
}

/** A 404 from the video reads means "not analysed yet" — an empty state, not a failure. */
const notAnalysed = (error: unknown) => error instanceof ApiError && error.status === 404

export function useVideoPlayback(caseId: string, documentId: string | null) {
  return useQuery<VideoPlaybackResponse>({
    queryKey: videoKeys.playback(caseId, documentId ?? ''),
    queryFn: () => videoApi.playback(caseId, documentId as string),
    enabled: Boolean(caseId && documentId),
    retry: (count, error) => !notAnalysed(error) && count < 2,
    // The ticket in the URL lives for hours; a fresh one on every open is enough.
    staleTime: 10 * 60_000,
  })
}

export function useVideoTracks(caseId: string, documentId: string | null) {
  return useQuery({
    queryKey: videoKeys.tracks(caseId, documentId ?? ''),
    queryFn: () => videoApi.tracks(caseId, documentId as string),
    enabled: Boolean(caseId && documentId),
    retry: (count, error) => !notAnalysed(error) && count < 2,
    staleTime: 60_000,
  })
}

/** Why the operator's time was not saved: the role refuses it, or the request failed. */
export type RecordedAtError = 'forbidden' | 'failed'

/** Setting the operator's time: the write, whether one is in flight, why the last was refused. */
export interface RecordedAtEdit {
  /** Save the operator's time (ISO 8601), or clear it with null; resolves true once saved. */
  save: (recordedAt: string | null) => Promise<boolean>
  isSaving: boolean
  error: RecordedAtError | null
}

/** When the recording starts and how the page knows it, with the control that sets it (slice 06). */
export interface RecordedAtState {
  recording: RecordedTime
  /** For a collaborator or an owner; null for a viewer, who is offered nothing (PRD-0032 SC-036). */
  edit: RecordedAtEdit | null
}

export interface UseDocumentVideoResult {
  /** The copy's clock, size and ticketed URL; null before it loads or without an analysis. */
  playback: VideoPlaybackResponse | null
  recordedAt: RecordedAtState
  /** The document was never analysed as a video: the page says so instead of playing. */
  notAnalysed: boolean
  isLoading: boolean
  isError: boolean
  tracks: VideoTrack[]
  /** The plates' passes (slice 07); empty while none was read or on an older API. */
  plateTracks: VideoPlateTrack[]
  duration: number
  /** The pass emphasised on the player and the strip — the selected face's, or a box picked. */
  selectedTrackId: number | null
  selectTrack: (trackId: number | null) => void
  /** What "Play pass" last asked for; the player runs it once per nonce. */
  playRequest: PassPlayRequest | null
  /** "Play pass" on a face's row: select it and run its pass with a second on either side. */
  playPass: (faceId: string) => void
  /** "Play pass" on a plate's row: run its span with a second on either side, selecting nothing. */
  playSpan: (from: number, to: number) => void
}

/**
 * The video state of one open document. The selection is shared with the faces section: picking
 * a box selects the face's row, picking a row emphasises its box — one selection, as on a photo.
 * For a document that is no video nothing is fetched (*enabled* false).
 */
/** What the player starts with on its own: a face's pass, or a window of the recording. */
export type AutoPlay = { faceId: string } | { from: number; to: number | null } | null

export function useDocumentVideo(
  caseId: string,
  documentId: string | null,
  enabled: boolean,
  faces: { selectedFaceId: string | null; select: (faceId: string | null) => void },
  /**
   * What to play as soon as the player is up, once per open: a face's pass — "Play pass" on a
   * card of the identity panel (PRD-0074 slice 04) — or the window a search hit names (slice 08).
   */
  autoPlay: AutoPlay = null
): UseDocumentVideoResult {
  const readId = enabled ? documentId : null
  const playback = useVideoPlayback(caseId, readId)
  const tracksQuery = useVideoTracks(caseId, readId)
  const recordedAt = useRecordedAt(caseId, documentId, playback.data)
  const tracks = useMemo(() => tracksQuery.data?.tracks ?? [], [tracksQuery.data])
  const plateTracks = useMemo(() => tracksQuery.data?.plates ?? [], [tracksQuery.data])
  const duration = playback.data?.duration_s ?? tracksQuery.data?.duration_s ?? 0
  // A pass without a face has no row to select: its selection lives here alone.
  const [pickedTrackId, setPickedTrackId] = useState<number | null>(null)
  const [playRequest, setPlayRequest] = useState<PassPlayRequest | null>(null)

  const selectedTrackId = trackOfFace(tracks, faces.selectedFaceId)?.track_id ?? pickedTrackId

  const selectTrack = useCallback(
    (trackId: number | null) => {
      const track = tracks.find(entry => entry.track_id === trackId) ?? null
      setPickedTrackId(track && !track.face ? track.track_id : null)
      faces.select(track?.face?.face_id ?? null)
    },
    [tracks, faces.select]
  )

  const playPass = useCallback(
    (faceId: string) => {
      const track = trackOfFace(tracks, faceId)
      if (!track) return
      faces.select(faceId)
      setPickedTrackId(null)
      setPlayRequest(current => passPlayRequest(track, (current?.nonce ?? 0) + 1, duration))
    },
    [tracks, faces.select, duration]
  )

  const playSpan = useCallback(
    (from: number, to: number) => {
      setPlayRequest(current => spanPlayRequest(from, to, (current?.nonce ?? 0) + 1, duration))
    },
    [duration]
  )

  // Once per open, as soon as what it needs is in: a pass needs the tracks, a window the playback copy.
  const autoPlayed = useRef<string | null>(null)
  useEffect(() => {
    if (!autoPlay || !documentId) return
    const ready = 'faceId' in autoPlay ? tracks.length > 0 : Boolean(playback.data)
    if (!ready) return
    const key = `${documentId}:${JSON.stringify(autoPlay)}`
    if (autoPlayed.current === key) return
    autoPlayed.current = key
    if ('faceId' in autoPlay) playPass(autoPlay.faceId)
    else playSpan(autoPlay.from, autoPlay.to ?? Number.POSITIVE_INFINITY)
  }, [autoPlay, documentId, tracks, playback.data, playPass, playSpan])

  return {
    playback: playback.data ?? null,
    recordedAt,
    notAnalysed: notAnalysed(playback.error) || notAnalysed(tracksQuery.error),
    isLoading: playback.isLoading || tracksQuery.isLoading,
    isError:
      (playback.isError && !notAnalysed(playback.error)) ||
      (tracksQuery.isError && !notAnalysed(tracksQuery.error)),
    tracks,
    plateTracks,
    duration,
    selectedTrackId,
    selectTrack,
    playRequest,
    playPass,
    playSpan,
  }
}

/** A refused write of the operator's time: the role, or anything else. */
const refusal = (error: unknown): RecordedAtError =>
  error instanceof ApiError && error.status === 403 ? 'forbidden' : 'failed'

/**
 * The time of recording (slice 06): what the playback read shows, and the operator's write —
 * the read is patched in place with the answer (a refetch would mint a new ticketed URL and
 * reload the player), and the timeline's items re-read, since every pass and scene of the
 * recording moves with its start (PRD-0074 SC-015).
 */
function useRecordedAt(
  caseId: string,
  documentId: string | null,
  playback: VideoPlaybackResponse | undefined
): RecordedAtState {
  const queryClient = useQueryClient()
  const write = useMutation({
    mutationFn: (recordedAt: string | null) =>
      videoApi.setRecordedAt(caseId, documentId as string, recordedAt),
    onSuccess: (answer, recordedAt) => {
      queryClient.setQueryData<VideoPlaybackResponse>(
        videoKeys.playback(caseId, documentId ?? ''),
        current =>
          current && {
            ...current,
            recorded_at: answer.recorded_at,
            recorded_at_source: answer.recorded_at_source,
            recorded_at_candidates: { ...current.recorded_at_candidates, operator: recordedAt },
          }
      )
      void queryClient.invalidateQueries({ queryKey: pureTimelineKeys.case(caseId) })
    },
  })
  const { mutateAsync, reset, status } = write
  const save = useCallback(
    async (recordedAt: string | null) => {
      if (!documentId) return false
      try {
        await mutateAsync(recordedAt)
        return true
      } catch {
        return false
      }
    },
    [documentId, mutateAsync]
  )
  // A refusal belongs to the document it was for: the overlay keeps this hook across documents.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on a document change only
  useEffect(() => {
    if (status !== 'idle') reset()
  }, [documentId])
  return {
    recording: recordedTimeOf(playback),
    edit:
      playback && canDecideFaces(playback.member_role)
        ? { save, isSaving: write.isPending, error: write.isError ? refusal(write.error) : null }
        : null,
  }
}
